import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Match } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { useLanguage } from "#i18n.tsx";
import {
  kitchenPresetSchema,
  kitchenSettingsSchema,
  kitchenTagColorSchema,
  type KitchenPreset,
  type KitchenTagColor,
} from "../../../packages/common/src/schemas/kitchen-notes.ts";

/** Merchant-owned preset editing; each explicit action saves atomically, never silently retries. */
export function KitchenPresetSettings({
  merchantId,
}: {
  readonly merchantId: string;
}) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<KitchenPreset>();
  const [label, setLabel] = useState("");
  const [color, setColor] = useState<KitchenTagColor>("red");
  const [formError, setFormError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [needsReload, setNeedsReload] = useState(false);
  const path = `/merchants/${encodeURIComponent(merchantId)}/kitchen-settings`;
  const settingsQueryKey = ["merchants", merchantId, "kitchen-settings"] as const;
  const settingsQuery = useQuery<
    typeof kitchenSettingsSchema.Type,
    ApiError
  >({
    queryKey: settingsQueryKey,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({ path, schema: kitchenSettingsSchema, signal }),
        signal,
      ),
  });
  const settings = settingsQuery.data;
  const resetEditor = () => {
    // A failed edit may have targeted a preset that was removed elsewhere.
    // Reload explicitly starts a fresh editor against the committed list.
    setEditing(undefined);
    setLabel("");
    setNotice(undefined);
    setFormError(undefined);
    setNeedsReload(false);
  };
  const saveMutation = useMutation({
    retry: false,
    mutationFn: ({
      presets,
      expectedRevision,
    }: {
      readonly presets: readonly KitchenPreset[];
      readonly expectedRevision: string;
    }) =>
      runEffectMutation(
        api.requestEffect({
          path,
          method: "PUT",
          body: {
            presets,
            expected_revision: expectedRevision,
          },
          schema: kitchenSettingsSchema,
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(settingsQueryKey, result);
      setEditing(undefined);
      setLabel("");
      setNotice(
        "Kitchen presets saved. Instructions already on orders are unchanged.",
      );
    },
    onError: () => {
      setNeedsReload(true);
    },
  });
  const pending = settingsQuery.isFetching || saveMutation.isPending;
  const editorError =
    formError ??
    (saveMutation.error
      ? `${saveMutation.error.message} Reload presets before making another change.`
      : undefined);
  const load = async () => {
    const result = await settingsQuery.refetch();
    if (result.isSuccess) {
      saveMutation.reset();
      resetEditor();
    }
  };
  const save = (presets: readonly KitchenPreset[]) => {
    if (!settings || pending || needsReload) return;
    const parsed = decodeUnknownOption(kitchenSettingsSchema, {
      revision: settings.revision,
      presets,
    });
    if (!parsed) {
      setFormError(
        "Use unique labels of 1–40 characters and no more than 24 presets.",
      );
      return;
    }
    setFormError(undefined);
    setNotice(undefined);
    saveMutation.reset();
    saveMutation.mutate({
      presets: parsed.presets,
      expectedRevision: settings.revision,
    });
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settings) return;
    const parsed = decodeUnknownOption(kitchenPresetSchema, {
      id: editing?.id ?? crypto.randomUUID(),
      label,
      color,
    });
    if (!parsed) {
      setFormError("Enter a preset label of 1–40 characters.");
      return;
    }
    save(
      editing
        ? settings.presets.map((preset) =>
            preset.id === editing.id ? parsed : preset,
          )
        : [...settings.presets, parsed],
    );
  };
  return (
    <section className="settings-card kitchen-preset-settings">
      <h2>{t("presets.title")}</h2>
      <p>{t("presets.description")}</p>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {Match.value(settingsQuery).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("presets.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <>
                <p className="alert" role="alert">
                  {failure.message}
                </p>
                <button
                  className="secondary"
                  disabled={pending}
                  onClick={() => void load()}
                >
                  {t("presets.reload")}
                </button>
              </>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data: settings }) => (
          <>
            {editorError && (
              <p className="alert" role="alert">
                {editorError}
              </p>
            )}
            {needsReload && (
              <button
                className="secondary"
                disabled={pending}
                onClick={() => void load()}
              >
                {t("presets.reload")}
              </button>
            )}
            {settings.presets.length === 0 && (
            <p>{t("presets.none")}</p>
            )}
            <ul className="preset-settings-list">
              {settings.presets.map((preset) => (
              <li key={preset.id}>
                <span
                  className={`instruction-badge instruction-${preset.color}`}
                >
                  {preset.label}
                </span>
                <div className="button-row">
                  <button
                    className="secondary"
                    disabled={pending || needsReload}
                    aria-label={`Edit preset ${preset.label}`}
                    onClick={() => {
                      setEditing(preset);
                      setLabel(preset.label);
                      setColor(preset.color);
                      setFormError(undefined);
                    }}
                  >
                    {t("presets.editAction")}
                  </button>
                  <button
                    className="secondary"
                    disabled={pending || needsReload}
                    aria-label={`Remove preset ${preset.label}`}
                    onClick={() =>
                      save(
                        settings.presets.filter(
                          (item) => item.id !== preset.id,
                        ),
                      )
                    }
                  >
                    {t("presets.removeAction")}
                  </button>
                </div>
              </li>
              ))}
            </ul>
            <form onSubmit={submit} className="preset-form">
            <h3>{editing ? t("presets.edit") : t("presets.add")}</h3>
            <label className="field">
              {t("presets.label")}
              <input
                value={label}
                maxLength={40}
                required
                disabled={pending || needsReload}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="For example: No egg"
                autoComplete="off"
              />
            </label>
            <fieldset className="preset-colors">
              <legend>{t("presets.colour")}</legend>
              {kitchenTagColorSchema.literals.map((option) => (
                <label
                  className={`instruction-preset instruction-${option}`}
                  key={option}
                >
                  <input
                    type="radio"
                    name="tagColor"
                    value={option}
                    checked={color === option}
                    disabled={pending || needsReload}
                    onChange={() => setColor(option)}
                  />
                  {option[0].toUpperCase() + option.slice(1)}
                </label>
              ))}
            </fieldset>
            <div className="button-row">
              <button
                className="primary"
                type="submit"
                disabled={
                  pending ||
                  needsReload ||
                  (!editing && settings.presets.length >= 24)
                }
              >
                {pending
                  ? t("presets.saving")
                  : editing
                    ? t("presets.save")
                    : t("presets.add")}
              </button>
              {editing && (
                <button
                  className="secondary"
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setEditing(undefined);
                    setLabel("");
                  }}
                >
                  {t("presets.cancelEdit")}
                </button>
              )}
            </div>
            </form>
          </>
        )),
        Match.exhaustive,
      )}
    </section>
  );
}
